import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Spinner from '@risk-smart/themed-cloudscape-components/spinner';
import Button from '@risksmart-app/components/src/button';
import { useGetGuidParam } from '@risksmart-app/components/src/routes/routes.utils';
import type { FC } from 'react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import type { ObjectToolbarConfig } from 'src/blocks/object-toolbar';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import ControlledTabs from 'src/components/controlled-tabs';
import DeleteModal from 'src/components/delete-modal';
import { PageLayout } from 'src/layouts';
import { useGetDetailPath } from 'src/routes/useGetDetailParentPath';

import { useDeleteUserGroups } from '@/hooks/mutations/user-group';
import { useGetUserGroupById } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useDeleteResultNotification } from '@/hooks/useMutationResultNotification';
import { settingsGroupsUrl } from '@/utils/urls';

import { useTabs } from './config';

type Props = {
  activeTabId: 'details' | 'members';
};

const Page: FC<Props> = ({ activeTabId }) => {
  const { t } = useTranslation(['common'], { keyPrefix: 'userGroups' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const userGroupId = useGetGuidParam('groupId');
  const detailPath = useGetDetailPath(userGroupId);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const navigate = useNavigate();

  const { data, loading } = useGetUserGroupById({
    queryArgs: { id: userGroupId },
  });

  const { deleteUserGroups, loading: deleteLoading } = useDeleteUserGroups();

  const userGroup = data?.user_group[0];
  const tabs = useTabs(userGroup, detailPath);

  const onDelete = useDeleteResultNotification({
    entityName: t('entity_name'),
    failureAction: () => {
      setIsDeleteModalVisible(false);
    },
    asyncAction: async () => {
      await deleteUserGroups({
        UserGroupIds: [userGroupId],
      });
      setIsDeleteModalVisible(false);
      navigate(settingsGroupsUrl());

      return true;
    },
  });

  const deleteDisabled =
    (userGroup?.approvers_aggregate?.aggregate?.count || 0) > 0;

  // Object-level toolbar (CUS-175) — flag-gated. Replaces the PageLayout header
  // with the object toolbar; the Delete action moves into the toolbar's danger
  // slot (disabled while the group still has approvers, matching the legacy
  // button). Legacy `actions` below stay as the flag-off fallback.
  const toolbarConfig: ObjectToolbarConfig = useMemo(
    () => ({
      header: { title: userGroup?.Name ?? '' },
      actions: {
        danger: [
          {
            id: 'delete',
            label: t('delete'),
            iconName: 'trash-01',
            onClick: () => setIsDeleteModalVisible(true),
            disabled: deleteDisabled,
          },
        ],
      },
    }),
    [userGroup?.Name, deleteDisabled, t]
  );

  return (
    <PageLayout
      title={userGroup?.Name}
      meta={{
        title: 'User Group',
      }}
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={toolbarConfig} />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <Button
            variant={'normal'}
            formAction={'none'}
            onClick={() => {
              setIsDeleteModalVisible(true);
            }}
            disabled={deleteDisabled}
          >
            {t('delete')}
          </Button>
        </SpaceBetween>
      }
    >
      {loading ? (
        <Spinner />
      ) : (
        <ControlledTabs
          activeTabId={activeTabId}
          tabs={tabs}
          variant={'container'}
        />
      )}
      <DeleteModal
        loading={deleteLoading}
        isVisible={isDeleteModalVisible}
        header={t('delete')}
        onDelete={onDelete}
        onDismiss={() => {
          setIsDeleteModalVisible(false);
        }}
      >
        {t('confirm_delete_message')}
      </DeleteModal>
    </PageLayout>
  );
};

export default Page;
